import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { DEFAULT_COLUMNS_OPTIONS } from './utils';
import useCallbackRef from 'hooks/useCallbackRef';

import './ListingCreationSelect.styles.scss';
import './ListingCreationSelect.styles.responsive.scss';

export type ListingCreationSelectOption<T = string> = {
  label: string;
  value: T;
};

type Options<T = string | string[]> = {
  options: Array<ListingCreationSelectOption>;
  value: T;
  defaultValue?: T;
  columns?: number;
  withShowMore?: boolean;
  disabled?: boolean;
  onChange: (val: T) => void;
  customOnChange?: (val: T) => void;
};

export type ListingCreationSelectProps<T> = {
  className?: string;
  styleType?: 'primary' | 'secondary';
} & Options<T>;

function ListingCreationSelect<T extends string | string[]>(
  props: ListingCreationSelectProps<T>,
) {
  const {
    className,
    options = [],
    value,
    columns = DEFAULT_COLUMNS_OPTIONS,
    withShowMore = true,
    styleType = 'primary',
    disabled,
    onChange,
    customOnChange,
  } = props;
  const { t } = useTranslation();

  const classes = classNames(
    'listing-creation-select',
    { disabled },
    styleType,
    className,
  );

  const [container, containerRef] = useCallbackRef<HTMLElement>(null);

  const [lastOptionIndex, setLastOptionIndex] = useState(
    Math.min(
      options.length - 1,
      options.length > columns ? columns - 2 : columns - 1,
    ),
  );

  useEffect(() => {
    container?.style.setProperty('--lcs-number-of-columns', `${columns}`);
  }, [columns, container?.style]);

  const handleOnOtherClick = useCallback(() => {
    if (disabled) return;
    setLastOptionIndex((old) => old + columns);
  }, [columns, disabled]);

  const graduallOptionsDisplay = useMemo(
    () =>
      options
        .slice(0, lastOptionIndex + 1)
        .map(({ value: optionValue, label }) => (
          <Button
            key={optionValue}
            onClick={() => {
              const changeVal = Array.isArray(value)
                ? value.includes(optionValue)
                  ? value.filter((el) => el !== optionValue)
                  : [...value, optionValue]
                : optionValue;

              onChange(changeVal as T);
              customOnChange?.(changeVal as T);
            }}
            styleType="secondary"
            variant="outline"
            className={value.includes(optionValue) && 'selected'}
          >
            {t(label)}
          </Button>
        )),
    [lastOptionIndex, options, value, customOnChange, onChange, t],
  );

  return (
    <div className={classes} ref={containerRef}>
      {withShowMore ? (
        <>
          {graduallOptionsDisplay}
          {lastOptionIndex + 1 < options.length && (
            <Button
              onClick={handleOnOtherClick}
              styleType="secondary"
              variant="outline"
            >
              {t('other')}
            </Button>
          )}
        </>
      ) : (
        options.map(({ value: optionValue, label }) => (
          <Button
            key={optionValue}
            onClick={() => {
              if (disabled) return;
              const changeVal = Array.isArray(value)
                ? value.includes(optionValue)
                  ? value.filter((el) => el !== optionValue)
                  : [...value, optionValue]
                : optionValue;

              onChange(changeVal as T);
              customOnChange?.(changeVal as T);
            }}
            styleType="secondary"
            variant="outline"
            className={value.includes(optionValue) && 'selected'}
          >
            {t(label)}
          </Button>
        ))
      )}
    </div>
  );
}

export default ListingCreationSelect;
